import { Component, OnDestroy, ViewChild, ElementRef, Input, ChangeDetectorRef, Renderer2} from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';

import { Observable, Subscription } from 'rxjs';
import GPT3Tokenizer from 'gpt3-tokenizer';
import { MessageService } from 'primeng/api';
import { isEqual, union, isNil } from 'lodash';

import { AIChatCompletionApiModel, AIChatCompletionMessageApiModel } from '../../../../api/ai/model/AI-chat-completion-api.model';
import { ChatMessageTypeModel, ChatRole } from '../../../../api/ai/enums/AI-chat-completion.enum';

import { TextInputComponent } from '../../../../Common/Forms/components/TextInput/text-input.component';
import { ActionKeys } from '../../../../Common/Forms/enums/keys.enum';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { AbstractAiDialogComponent } from '../../../../Common/Dialogs/components/AbstractAIDialog/Abstract-AI-dialog.component';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';

import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { ConfigService } from '../../../../Core/Config/services/config.service';

import { WireService } from '../../services/wire.service';
import { WirePlainTextService } from '../../services/plain-text.service';
import { WireModel } from '../../models/wire.model';
import { WireHeaderAssessmentMessage, WireHeaderEditorService } from './wire-header-editor.service';

export enum InfoType {
    dateline = 'dateline',
    companyName = 'companyName',
    headline = 'headline',
    subHeadline = 'subHeadline'
}

enum EmailIcons {
    invalid = 'fa-regular fa-duotone fa-circle-xmark',
    exclamation = 'fa-regular fa-duotone fa-circle-exclamation',
    check = 'fa-regular fa-duotone fa-circle-check'
}
enum AISuggestionTypes {
    headline = 'headline',
    subHeadline = 'subHeadline',
}

@Component({
    selector: 'agpr-wire-header-recipient-dialog',
    templateUrl: './wire-header-editor.component.html',
    styleUrls: ['./wire-header-editor.component.scss'],
    providers: [
        WireHeaderEditorService
    ],
    animations: [
        trigger('panelState', [
            state('hidden', style({
                opacity: 0,
                transform: 'scaleY(0)'
            })),
            state('visible', style({
                opacity: 1,
                transform: 'scaleY(1)'
            })),
            transition('visible => hidden', animate('.1s linear')),
            transition('hidden => visible', animate('.15s cubic-bezier(0, 0, 0.2, 1)'))
        ])
    ],
    standalone: false
})
export class WireHeaderEditorComponent extends AbstractAiDialogComponent implements OnDestroy {

    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;
    @Input() openAiPermission: boolean;
    @Input() title: string = '';

    readonly minPanelWidth = 980;
    readonly maxPanelWidth = 1180;
    readonly infoTypes = InfoType;
    readonly buttonTypes = ButtonTypesEnum;

    hideSlideOverlay: boolean = false;

    loading: boolean = false;
    doneStreaming = true;
    hasGenerated: boolean = false;
    hasKeyMessage: boolean = false;
    disclaimerCollapsed: boolean = false;
    generatedContent: string = '';

    bodyPlainText: string = '';

    tokenizer = new GPT3Tokenizer({ type: 'gpt3' });

    headlinePrompts: string[] = [];
    showHeadlinePrompts: boolean = false;
    headlinePromptsLoading: boolean = false;
    headlineAiError: boolean = false;
    headlineSelected: number;
    subHeadlinePrompts: string[] = [];
    showSubHeadlinePrompts: boolean = false;
    subHeadlinePromptsLoading: boolean = false;
    subHeadlineAiError: boolean = false;
    subHeadlineSelected: number;
    doneSettingBodyText: boolean = false;
    noBodyText: boolean = false;
    documentClickListener: any;

    aiQuickTipInfo: string = '';

    readonly maxTokenLimit = 1024;
    readonly headlineTemplateLength = 200;
    readonly headlineStringMatch: string = 'headline';
    readonly subHeadlineStringMatch: string = 'subHeadline';

    subscriptions: Subscription[] = [];

    wire: WireModel;

    headline: string;
    headlineError: boolean = false;
    headlineByteLength: number = 0; // News Direct has this length limit of 200 bytes
    subHeadline: string;
    subHeadlineError: boolean = false;
    dateline: string;
    datelineError: boolean = false;
    isDatelineFocused: boolean = false;
    companyName: string;
    companyNameError: boolean = false;

    assessmentMessages: WireHeaderAssessmentMessage;

    emailIcon: EmailIcons | null = null;

    infoContent = {
        label: '',
        hints: []
    };

    @ViewChild('datelineInput') datelineInput: TextInputComponent;
    @ViewChild('companyNameInput') companyNameInput: TextInputComponent;
    @ViewChild('headlineInput') headlineInput: TextInputComponent;
    @ViewChild('subHeadlineInput') subHeadlineInput: TextInputComponent;

    @ViewChild('datelineLiveRegion') datelineLiveRegion: ElementRef;
    @ViewChild('companyNameLiveRegion') companyNameLiveRegion: ElementRef;
    @ViewChild('headlineLiveRegion') headlineLiveRegion: ElementRef;
    @ViewChild('subHeadlineLiveRegion') subHeadlineLiveRegion: ElementRef;


    constructor(
        public coreDialogService: CoreDialogService,
        public configService: ConfigService,
        public wireService: WireService,
        public localizationService: LocalizationService,
        public headerEditorService: WireHeaderEditorService,
        private messageService: MessageService,
        public renderer: Renderer2,
        private cd: ChangeDetectorRef,
        public plainTextService: WirePlainTextService
    ) {
        super(coreDialogService, 'WireHeaderEditor');

        this.subscriptions.push(
            this.wireService.currentResource$.subscribe(wire => {
                this.wire = wire;
                this.bodyPlainText = this.plainTextService.generateBodyPlainText(this.wire.blocks);

                this.headline = !!wire.headline ? '' + wire.headline : undefined;
                this.subHeadline = !!wire.subHeadline ? '' +  wire.subHeadline : undefined;
                this.dateline = !!wire.dateline ? '' + wire.dateline : undefined;
                this.companyName = !!wire.companyName ? '' + wire.companyName : undefined;
            }),
            this.headerEditorService.getHeaderAssessmentMessages().subscribe(assessmentMessages => {
                this.assessmentMessages = assessmentMessages;
            })
        );

        this.bindDocumentClickListener();
    }

    /**
     * panelOpened
     * this is called when the animation for the panel is done opening
     * we need this because if we would call setMaxLimitOnBodyText before the animation, the panel would take "time" to open, and didn't look right
     * this way, the panel opens, and we have a loading indicator to show it.
     */
    panelOpened() {
        if (this.openAiPermission) {
            this.setMaxLimitOnBodyText();
            if (!this.tokenizer.encode(this.bodyPlainText)?.text?.length) {
                this.noBodyText = true;
            }
        } else {
            this.doneSettingBodyText = true;
        }
    }

    /**
     * setMaxLimitOnBodyText
     * this function is to trim the newswire body text, if it's too long (we don't want to hit the max token limit)
     * it will loop through, and keep triming until the token limit is matched, currently it removes 500 characters from the end at a time.
     */
    setMaxLimitOnBodyText() {
        while (this.tokenizer.encode(this.bodyPlainText)?.text?.length > (this.maxTokenLimit - this.headlineTemplateLength)) {
            this.bodyPlainText = this.bodyPlainText.slice(0, -500);
        }
        this.doneSettingBodyText = true;
    }

    generate(type: string): void {
        this.generatedContent = '';
        this.loading = true;
        this.doneStreaming = false;

        this.fetchData(this.configService.getConfig().apiUrl + '/ai/chat/completions', type);
    }
    getRequestModel(type): AIChatCompletionApiModel {

        const message: AIChatCompletionMessageApiModel = {
            role: ChatRole.USER,
            chatMessageType: ChatMessageTypeModel.HEADLINE_GENERATION,
            message : ''
        };

        if (type === AISuggestionTypes.headline) {
            message.message = this.getHeadlineRequestModel();
            return { messages: [message] };
        } else if (type === AISuggestionTypes.subHeadline) {
            message.message = this.getSubHeadlineRequestModel();
            return { messages: [message] };
        }
    }
    onComplete(type: string){
        if (type === AISuggestionTypes.headline) {
            return this.onHeadlineComplete();
        } else if (type === AISuggestionTypes.subHeadline) {
            return this.onSubHeadlineComplete();
        }
    }
    handleError(type: string) {
        if (type === AISuggestionTypes.headline) {
            return this.handleHeadlineError();
        } else if (type === AISuggestionTypes.subHeadline) {
            return this.handleSubHeadlineError();
        }
    }

    //#region -> subject line
    generateHeadline() {
        this.showInfo(this.infoTypes.headline);
        if (!this.headlinePromptsLoading && this.openAiPermission && !this.noBodyText) {
            document.getElementById('headerEditor.headlineInput')?.focus();
            this.headlineSelected = null;
            this.headlinePrompts = [];
            this.headlinePromptsLoading = true;
            this.showHeadlinePrompts = true;
            this.headlineError = false;
            this.headlineAiError = false;
            this.subHeadlineAiError = false;
            this.showSubHeadlinePrompts = false;
            this.generate(AISuggestionTypes.headline);
        }
    }
    getHeadlineRequestModel(): string {
        const message = {
            body: this.bodyPlainText,
            format: 'HEADLINE_NEWSWIRE'
        };
        return JSON.stringify(message);
    }
    onHeadlineComplete(){
        for (let i = 1; i <= 3; i++) {
            // Safari does not support lookbehind (?<=\/)
            // get data between <headline1> & </headline1>
            let slMatch = new RegExp(`(?:<${this.headlineStringMatch}${i}>).*?(?=\s*<\/${this.headlineStringMatch}${i}>)`, 'gs');
            let headline = this.generatedContent.trim().match(slMatch);

            // sometimes the AI does not return the closing tags, so we will get all the data between <headline1> & <headline2>
            if (!headline) {
                slMatch = new RegExp(`(?:<${this.headlineStringMatch}${i}>).*?(?=\s*<${this.headlineStringMatch}${i+1}>)`, 'gs');
                headline = this.generatedContent.trim().match(slMatch);
            }

            // get data after <headline3>
            if (!headline && (i === 3)) {
                slMatch = new RegExp(`(?:<${this.headlineStringMatch}${i}>).*`, 'gs');
                headline = this.generatedContent.trim().match(slMatch);
            }

            if (headline?.length) {
                this.headlinePrompts = union(this.headlinePrompts, headline);
                this.headlinePrompts.forEach((line, index) => {
                    this.headlinePrompts[index] = line.replace(`<${this.headlineStringMatch}${i}>`, '');
                });
            }
        }
        this.headlinePromptsLoading = false;
        (this.headlineLiveRegion.nativeElement as HTMLElement).textContent = this.localizationService.transform(
            this.headlinePrompts.length > 0 ? 'Common.input.suggestionCompleteMessage' : 'Common.input.noSuggestionGeneratedMessage'
        );
    }
    handleHeadlineError() {
        this.messageService.add(
            {
                severity: messageSeverityEnum.error,
                summary: this.localizationService.transform('Common.dialog.aiHelper.error.title'),
                detail: this.localizationService.transform('Amplify.wire.headerEditor.headlineError')
            }
        );
        this.loading = false;
        this.hasGenerated = true;
        this.headlineAiError = true;
        this.headlinePromptsLoading = false;
        this.showHeadlinePrompts = false;
    }
    setHeadline(suggestion) {
        this.headline = suggestion;
        this.showHeadlinePrompts = false;
        this.validateHeadline(null);
        this.headlineKeyUp(null);
    }
    //#endregion

    //#region -> preview text
    generateSubHeadline() {
        this.showInfo(this.infoTypes.subHeadline);
        if (!this.subHeadlinePromptsLoading && this.openAiPermission && !this.noBodyText) {
            document.getElementById('headerEditor.subHeadlineInput')?.focus();
            this.subHeadlineSelected = null;
            this.subHeadlinePrompts = [];
            this.subHeadlinePromptsLoading = true;
            this.showSubHeadlinePrompts = true;
            this.headlineAiError = false;
            this.subHeadlineAiError = false;
            this.showHeadlinePrompts = false;
            this.generate(AISuggestionTypes.subHeadline);
        }
    }
    getSubHeadlineRequestModel(): string {
        const message: any = {
            body: this.bodyPlainText,
            format: 'SUBHEADLINE_NEWSWIRE'
        };
        if (this.headline) {
            message.headingText = this.headline;
        }
        return JSON.stringify(message);
    }

    onSubHeadlineComplete(){
        for (let i = 1; i <= 3; i++) {
            // Safari does not support lookbehind (?<=\/)
            // get data between <subHeadline1> & </subHeadline1>
            let ptMatch = new RegExp(`(?:<${this.subHeadlineStringMatch}${i}>).*?(?=\s*<\/${this.subHeadlineStringMatch}${i}>)`, 'gs');
            let subHeadline = this.generatedContent.trim().match(ptMatch);

            // sometimes the AI does not return the closing tags, so we will get all the data between <subHeadline1> & <subHeadline2>
            if (!subHeadline) {
                ptMatch = new RegExp(`(?:<${this.subHeadlineStringMatch}${i}>).*?(?=\s*<${this.subHeadlineStringMatch}${i+1}>)`, 'gs');
                subHeadline = this.generatedContent.trim().match(ptMatch);
            }

            // get data after <subHeadline3>
            if (!subHeadline && (i === 3)) {
                ptMatch = new RegExp(`(?:<${this.subHeadlineStringMatch}${i}>).*`, 'gs');
                subHeadline = this.generatedContent.trim().match(ptMatch);
            }
            if (subHeadline?.length) {
                this.subHeadlinePrompts = union(this.subHeadlinePrompts, subHeadline);
                this.subHeadlinePrompts.forEach((line, index) => {
                    this.subHeadlinePrompts[index] = line.replace(`<${this.subHeadlineStringMatch}${i}>`, '');
                });
            }
        }
        this.subHeadlinePromptsLoading = false;
        (this.subHeadlineLiveRegion.nativeElement as HTMLElement).textContent = this.localizationService.transform(
            this.subHeadlinePrompts.length > 0 ? 'Common.input.suggestionCompleteMessage' : 'Common.input.noSuggestionGeneratedMessage'
        );
    }
    handleSubHeadlineError() {
        this.messageService.add(
            {
                severity: messageSeverityEnum.error,
                summary: this.localizationService.transform('Common.dialog.aiHelper.error.title'),
                detail: this.localizationService.transform('Amplify.wire.headerEditor.subHeadlineError')
            }
        );
        this.loading = false;
        this.hasGenerated = true;
        this.subHeadlineAiError = true;
        this.subHeadlinePromptsLoading = false;
        this.showSubHeadlinePrompts = false;
    }
    setSubHeadline(suggestion) {
        this.subHeadline = suggestion;
        this.showSubHeadlinePrompts = false;
        this.subHeadlineKeyUp(null);
    }

    updateHeader() {
        this.wire.headline = this.headline?.trim();
        this.wire.subHeadline = this.subHeadline?.trim();
        this.wire.dateline = this.dateline?.trim();
        this.wire.companyName = this.companyName?.trim();
        this.headerEditorService.updateHeader(this.assessmentMessages);
        this.wireService.currentResourceModified();
        this.closeDialog();
    }

    showInfo(infoType: InfoType) {
        this.infoContent.label = 'Amplify.wire.headerEditor.' + infoType;

        // The component could be made not to know the number of hints by using a prefix discovery system
        this.infoContent.hints = [];
        switch (infoType) {
            case InfoType.dateline:
                this.isDatelineFocused = true;
                for (let i = 1; i <= 5; i++) {
                    this.infoContent.hints.push('Amplify.wire.headerEditor.hints.dateline.' + i);
                }
                break;
            case InfoType.companyName:
                for (let i = 1; i <= 2; i++) {
                    this.infoContent.hints.push('Amplify.wire.headerEditor.hints.companyName.' + i);
                }
                break;
            case InfoType.headline:
                for (let i = 1; i <= 4; i++) {
                    this.infoContent.hints.push('Amplify.wire.headerEditor.hints.headline.' + i);
                }
                this.aiQuickTipInfo = 'Amplify.wire.headerEditor.quickTip.headline';
                break;
            case InfoType.subHeadline:
                for (let i = 1; i <= 4; i++) {
                    this.infoContent.hints.push('Amplify.wire.headerEditor.hints.subHeadline.' + i);
                }
                this.aiQuickTipInfo = 'Amplify.wire.headerEditor.quickTip.subHeadline';
                break;
        }
        this.setAssessmentMessageLiveRegion();
    }

    clearHints() {
        this.infoContent = {
            label: '',
            hints: []
        };
        this.aiQuickTipInfo = '';
    }

    setHeadlineIfEnter(event: KeyboardEvent, suggestion) {
        if (event?.key === ActionKeys.ENTER) {
            this.setHeadline(suggestion);
        }
    }

    headlineKeyUp(event: KeyboardEvent) {
        event?.stopImmediatePropagation();
        if (this.showHeadlinePrompts) {
            if (!this.headlinePromptsLoading) {
                if (event?.key === ActionKeys.ENTER) {
                    if (!isNil(this.headlineSelected) && this.headlinePrompts?.length) {
                        this.setHeadline(this.headlinePrompts[this.headlineSelected]);
                    }
                }
                if (event?.key === ActionKeys.DOWN) {
                    if (isNil(this.headlineSelected) || (this.headlineSelected >= this.headlinePrompts.length - 1)) {
                        this.headlineSelected = 0;
                    } else {
                        this.headlineSelected = Math.min(this.headlineSelected + 1, this.headlinePrompts.length - 1);
                    }
                }
                if (event?.key === ActionKeys.UP) {
                    if (!this.headlineSelected) {
                        this.headlineSelected = this.headlinePrompts.length - 1;
                    } else {
                        this.headlineSelected = Math.max(this.headlineSelected - 1, 0);
                    }
                }
            }
        } else {
            if (event?.key === ActionKeys.ENTER || event?.key === ActionKeys.DOWN) {
                this.subHeadlineInput.giveFocus();
            }
        }
        if (event?.key === ActionKeys.ESCAPE) {
            this.closeDialog();
        }

        this.assessmentMessages.headline = this.headerEditorService.assessHeadline(this.headline);
        // Ensure preview text (if entered) does not match new subject line
        this.assessmentMessages.subHeadline = this.headerEditorService.assessSubHeadline(this.subHeadline, this.headline);
        this.setAssessmentMessageLiveRegion();
        if (this.headlineAiError) {
            this.headlineAiError = !this.headline;
        }
    }

    validateHeadline(event) {
        this.headlineError = !this.headline;
        if (this.headlineAiError) {
            this.headlineAiError = !this.headline;
        }
        this.validateHeadlineByteLength(false);

        const headlineButton = document.getElementById('agpr_headerEditor_generateHeadline')?.firstChild;
        if (!isEqual(headlineButton, event?.relatedTarget)) {
            this.clearHints();
        } else {
            document.getElementById('headerEditor.headlineInput').focus();
        }
    }

    validateSubHeadline(event) {
        const subHeadlineButton = document.getElementById('agpr_headerEditor_generateSubHeadline')?.firstChild;
        if (!isEqual(subHeadlineButton, event?.relatedTarget)) {
            this.clearHints();
        } else {
            document.getElementById('headerEditor.subHeadlineInput').focus();
        }
    }

    validateHeadlineByteLength(validateOnChange: boolean) {
        if (this.headlineByteLength <= 200 && validateOnChange) {
            return;
        }
        this.headlineByteLength = (new TextEncoder().encode(this.headline)).length;
    }

    subHeadlineKeyUp(event: KeyboardEvent) {
        event?.stopImmediatePropagation();
        if (this.showSubHeadlinePrompts) {
            if (!this.subHeadlinePromptsLoading) {
                if (event?.key === ActionKeys.ENTER) {
                    if (!isNil(this.subHeadlineSelected) && this.subHeadlinePrompts?.length) {
                        this.setSubHeadline(this.subHeadlinePrompts[this.subHeadlineSelected]);
                    }
                }
                if (event?.key === ActionKeys.DOWN) {
                    if (isNil(this.subHeadlineSelected) || (this.subHeadlineSelected >= this.subHeadlinePrompts.length - 1)) {
                        this.subHeadlineSelected = 0;
                    } else {
                        this.subHeadlineSelected = Math.min(this.subHeadlineSelected + 1, this.subHeadlinePrompts.length - 1);
                    }
                }
                if (event?.key === ActionKeys.UP) {
                    if (!this.subHeadlineSelected) {
                        this.subHeadlineSelected = this.subHeadlinePrompts.length - 1;
                    } else {
                        this.subHeadlineSelected = Math.max(this.subHeadlineSelected - 1, 0);
                    }
                }
            }
        } else {
            if (event?.key === ActionKeys.UP) {
                this.headlineInput.giveFocus();
            }
            if (event?.key === ActionKeys.ENTER || event?.key === ActionKeys.DOWN) {
                this.companyNameInput.giveFocus();
            }
            if (event?.key === ActionKeys.ENTER) {
                this.updateHeader();
            }
        }
        this.assessmentMessages.subHeadline = this.headerEditorService.assessSubHeadline(this.subHeadline, this.headline);
        this.setAssessmentMessageLiveRegion();

        if (this.subHeadlineAiError) {
            this.subHeadlineAiError = !this.subHeadline;
        }
    }

    companyNameKeyUp(event: KeyboardEvent) {
        if (event.key === ActionKeys.UP) {
            this.subHeadlineInput.giveFocus();
        }
        if (event.key === ActionKeys.ENTER || event.key === ActionKeys.DOWN) {
            this.datelineInput.giveFocus();
        }
        if (event.key === ActionKeys.ESCAPE) {
            this.closeDialog();
        }
        this.assessmentMessages.companyName = this.headerEditorService.assessCompanyName(this.companyName);
        this.setAssessmentMessageLiveRegion();
    }

    validateCompanyName() {
        this.companyNameError = !this.companyName;
        this.updateEmailErrorFlag();
    }

    datelineKeyUp(event: KeyboardEvent) {
        if (event.key === ActionKeys.UP) {
            this.companyNameInput.giveFocus();
        }
        if (event.key === ActionKeys.ENTER || event.key === ActionKeys.DOWN) {
            this.updateHeader();
        }
        if (event.key === ActionKeys.ESCAPE) {
            this.closeDialog();
        }
        this.assessmentMessages.dateline = this.headerEditorService.assessDateline(this.dateline);
        this.setAssessmentMessageLiveRegion();
    }

    validateDateline() {
        if (this.isDatelineFocused) {
            return;
        }

        this.datelineError = !this.dateline;
        this.clearHints();
    }

    updateEmailErrorFlag() {
        this.clearHints();
    }

    /**
     * For accessibility purposes: set text content of aria-live region so SR can read it properly
     */
    setAssessmentMessageLiveRegion() {
        const textContent = this.localizationService.transform('Common.input.showWarningMessage');
        // set to blank by default so that when text content changes, SR will announce it
        (this.headlineLiveRegion.nativeElement as HTMLElement).textContent = '';
        (this.subHeadlineLiveRegion.nativeElement as HTMLElement).textContent = '';
        (this.datelineLiveRegion.nativeElement as HTMLElement).textContent = '';
        (this.companyNameLiveRegion.nativeElement as HTMLElement).textContent = '';

        if (this.assessmentMessages.headline.length > 0) {
            (this.headlineLiveRegion.nativeElement as HTMLElement).textContent = textContent;
        }
        if (this.assessmentMessages.subHeadline.length > 0) {
            (this.subHeadlineLiveRegion.nativeElement as HTMLElement).textContent = textContent;
        }
        if (this.assessmentMessages.dateline.length > 0) {
            (this.datelineLiveRegion.nativeElement as HTMLElement).textContent = textContent;
        }
        if (this.assessmentMessages.companyName.length > 0) {
            (this.companyNameLiveRegion.nativeElement as HTMLElement).textContent = textContent;
        }
    }

    dismissPromptsByFocus(event: FocusEvent): void {
        if (!event.relatedTarget || !(event.relatedTarget as HTMLElement).classList.contains('agpr-headerEditor-suggestions-item')) {
            this.showHeadlinePrompts = false;
            this.showSubHeadlinePrompts = false;
        }
    }

    doNotCloseHeadline(event) {
        event.stopPropagation();
        this.showHeadlinePrompts = true;
    }

    doNotCloseSubHeadline(event) {
        event.stopPropagation();
        this.showSubHeadlinePrompts = true;
    }

    bindDocumentClickListener() {
        if (!this.documentClickListener) {
            this.documentClickListener = this.renderer.listen('document', 'click', (event) => {
                const headlineInputClicked = isEqual(document.getElementById('headerEditor.headlineInput'), event?.target);
                const headlineLoadingClicked = isEqual(document.getElementById('agpr_headerEditor_headlineLoading'), event?.target);
                const generateHeadlineClicked = isEqual(document.getElementById('agpr_headerEditor_generateHeadline'), event?.target);
                const subHeadlineInputClicked = isEqual(document.getElementById('headerEditor.subHeadlineInput'), event?.target);
                const subHeadlineLoadingClicked = isEqual(document.getElementById('agpr_headerEditor_subHeadlineLoading'), event?.target);
                const generateSubHeadlineClicked = isEqual(document.getElementById('agpr_headerEditor_generateSubHeadline'), event?.target);

                // forsome reason it is returning the body when I click on the generate AI buttons
                const generateClicked = isEqual(document.body, event?.target);

                if (!headlineInputClicked && !headlineLoadingClicked && !generateHeadlineClicked && !generateClicked) {
                    this.showHeadlinePrompts = false;
                }
                if (!subHeadlineInputClicked && !subHeadlineLoadingClicked && !generateSubHeadlineClicked && !generateClicked) {
                    this.showSubHeadlinePrompts = false;
                }
                this.cd.markForCheck();
            });
        }
    }

    unbindDocumentClickListener() {
        if (this.documentClickListener) {
            this.documentClickListener();
            this.documentClickListener = null;
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
        this.unbindDocumentClickListener();
    }
}
